import { DEFAULT_HTML_ATTRIBUTES } from 'assets/data';
import classNames from 'classnames';
import { TIMEOUT_DELAY } from 'constants/general';
import { ATTRIB } from 'constants/test';
import {
  Attributes,
  DetailedHTMLProps,
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { runAfterDelay } from 'utils/helpers';
import { v4 as uuid } from 'uuid';

interface TextInputProps
  extends
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    Attributes {
  readonly additionalStyles?: string;
  readonly enableDebounce?: boolean;
  readonly label?: string;
  readonly describedById?: string;
  readonly error?: string;
  readonly labelStyles?: string;
}

const TextInput = forwardRef(
  (
    {
      className,
      additionalStyles,
      enableDebounce = false,
      onChange,
      label,
      describedById,
      id,
      error,
      labelStyles = 'flex flex-col ~text-xs/sm font-medium text-gray-700 dark:text-secondary-dark-900',
      ...rest
    }: TextInputProps,
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [debouncedValue, setDebouncedValue] = useState('');

    useEffect(() => {
      if (!enableDebounce) {
        return;
      }

      const handler = runAfterDelay(() => {
        onChange?.({
          target: { value: debouncedValue }
        } as React.ChangeEvent<HTMLInputElement>);
      }, TIMEOUT_DELAY.TEXT_INPUT_DEBOUNCE_DELAY);

      return () => clearTimeout(handler);
    }, [debouncedValue, enableDebounce, onChange]);

    useImperativeHandle(ref, () => ({
      getValue() {
        return inputRef.current?.value ?? '';
      },
      getInputHeight() {
        const offsetHeight = inputRef.current?.offsetHeight ?? 0;
        return offsetHeight;
      },
      focus() {
        inputRef.current?.focus();
      }
    }));

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (enableDebounce) {
          setDebouncedValue(event.target.value);
        } else {
          onChange?.(event);
        }
      },
      [enableDebounce, onChange]
    );

    const inputId = useMemo(
      () =>
        id ??
        `text-input-${label ? label.replace(/\s+/g, '-').toLowerCase() : uuid()}`,
      [id, label]
    );

    return (
      <label htmlFor={inputId} className={labelStyles}>
        {label}
        <input
          ref={inputRef}
          id={inputId}
          type='text'
          {...rest}
          aria-describedby={describedById}
          data-testid={ATTRIB.DATA_TESTID.TEXT_INPUT}
          className={classNames(
            className,
            'border border-primary-500 dark:border-secondary-dark-500 rounded-md ~px-1.5/2.5 ~py-1/1.5 text-black bg-white dark:bg-secondary-dark-300 dark:text-secondary-dark-900 shadow focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-dark-500 focus:outline-none w-full cursor-text',
            additionalStyles
          )}
          {...DEFAULT_HTML_ATTRIBUTES}
          onChange={handleChange}
        />
        <small
          className={`${error ? 'inline-flex' : 'hidden'} ~text=[0.45rem]/xs text-red-600 italic`}>
          {error}
        </small>
      </label>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
